import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import Section from './Section';

interface PricingProps {
  standalone?: boolean;
}

const Pricing: React.FC<PricingProps> = ({ standalone = false }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const systemTypes = [
    {
      title: 'LP・Webサイト〜業務アプリ',
      price: '10〜100',
      features: [
        'LP・コーポレートサイト: 10〜30万円',
        '業務Webアプリ: 30〜100万円',
        '月額保守: 1〜5万円/月'
      ]
    },
    {
      title: '基幹システム',
      price: '100〜300',
      features: [
        '受発注管理システム',
        '複数拠点対応',
        '段階的導入で初期負担を軽減'
      ]
    }
  ];


  const containerClass = standalone
    ? 'min-h-screen py-32'
    : 'py-20 lg:py-28';

  return (
    <Section id="pricing" className={`${containerClass} bg-[#F5F3EF]`}>
      <div ref={ref} className={`mx-auto max-w-6xl px-5 lg:px-6 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
            <span className="text-sm font-semibold text-[#B8860B]">料金の目安</span>
          </div>
          <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">
            「高い」と諦める前に<br />
            <span className="text-gradient">ご相談ください</span>
          </h2>
        </div>

        {/* アンカリング: 一般的な相場 */}
        <div className="rounded-2xl border border-[#D5D2CC] bg-gradient-to-b from-[#EDEBE6] to-[#F5F3EF] px-6 py-6 mb-8 text-center">
          <p className="text-sm text-[#7B8794] mb-3 font-medium">一般的な開発会社の相場</p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-[#9B4D3A]">
            <span className="line-through">LP制作 50〜150万円</span>
            <span className="line-through">業務アプリ 300〜1,000万円</span>
            <span className="line-through">基幹システム 1,000〜5,000万円</span>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="h-px w-8 bg-[#D5D2CC]" />
            <div className="h-px w-2 bg-[#D5D2CC]" />
            <div className="h-px w-8 bg-[#D5D2CC]" />
          </div>
          <p className="mt-4 text-lg font-bold text-[#1B2A4A]">Senriganなら、<span className="text-[#B8860B]">60〜80%OFF</span>で実現します</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          {systemTypes.map((type, index) => (
            <div
              key={index}
              className="rounded-2xl border border-[#E8E6E1] bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="bg-[#1B2A4A] px-6 py-4">
                <h3 className="text-lg font-bold text-[#F5F3EF]">{type.title}</h3>
              </div>
              <div className="p-6">
                <p className="text-3xl font-bold text-[#1B2A4A] mb-5">
                  {type.price}<span className="text-lg text-[#5A6577]">万円</span>
                </p>
                <ul className="space-y-3">
                  {type.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2.5 text-[#5A6577]">
                      <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B8860B]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href={standalone ? '/#contact' : '#contact'}
            className="inline-flex items-center gap-2 rounded-lg bg-[#B8860B] px-8 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20"
          >
            まずは無料相談（30分）
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </Section>
  );
};

export default Pricing;
